/**
 * Calendar arithmetic in one fixed time zone.
 *
 * ---------------------------------------------------------------------------
 * WHY A FIXED ZONE AND NOT THE VIEWER'S
 * ---------------------------------------------------------------------------
 * The countdown is shared: press a button on a phone and every other device
 * showing the page must agree. "Agree" has to mean the digits match, not just
 * the underlying instant - and a Y/M/D breakdown computed in the viewer's own
 * zone does NOT match across zones. Two people watching the same target from
 * Belgrade and Los Angeles would see different day counts for nine hours out
 * of every twenty-four, because the calendar day rolls over at different
 * instants. That reads as a bug in the sync, which is the worst kind of bug:
 * the thing that is actually working looks broken.
 *
 * So every calendar operation happens in ONE zone, `ZONE` below, on every
 * device. The anchor is a wall-clock time in Belgrade ("23 August, 11:00 PM"),
 * so Belgrade is the zone that makes the anchor mean what it says.
 *
 * ---------------------------------------------------------------------------
 * WHY NOT JUST DIVIDE BY 365.25
 * ---------------------------------------------------------------------------
 * That was the first version and it is wrong in a way you can watch happen.
 * With fixed-length "years" and "months" the display drifts against the
 * calendar, so a countdown anchored to 23 August shows 11 months 30 days on
 * the 23rd of the following September instead of a clean rollover, and the
 * month field jumps by two at some point every year to make the arithmetic
 * close. Nobody can tell you why. Months are not a duration; they are a
 * calendar operation, and they have to be done as one.
 *
 * ---------------------------------------------------------------------------
 * WHY NOT A DATE LIBRARY
 * ---------------------------------------------------------------------------
 * Temporal is not in workerd or in Safari yet, and pulling in a tz-aware date
 * library would be a multi-hundred-kilobyte dependency for a page whose entire
 * payload is otherwise a few kilobytes. `Intl.DateTimeFormat` already ships the
 * full IANA database in every browser and in the Workers runtime; the only
 * thing missing is the inverse direction (civil time -> instant), which is the
 * twenty lines in `instantOf`.
 *
 * Nothing in this file touches the DOM. It is imported by the Pages Function
 * as well as the bundle, so it must stay that way.
 */

/**
 * The one zone all calendar maths happens in.
 *
 * Changing this changes what the digits say for everyone, because it changes
 * where a "day" boundary falls - it is not a display preference.
 */
export const ZONE = 'Europe/Belgrade'

export interface Civil {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  second: number
}

/**
 * `hourCycle: 'h23'` rather than `hour12: false`.
 *
 * They are not synonyms: `hour12: false` with an en-US locale yields "24" for
 * midnight in several engines, so `civilOf` would hand back hour 24 on a date
 * that has already rolled over, and every day boundary would be off by one for
 * exactly one hour a night. `h23` is the one that means 00-23.
 */
const CIVIL_FORMAT = new Intl.DateTimeFormat('en-US', {
  timeZone: ZONE,
  hourCycle: 'h23',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit'
})

/** Wall-clock fields in `ZONE` for an instant. Whole seconds; ms are dropped. */
export function civilOf(ms: number): Civil {
  const parts = CIVIL_FORMAT.formatToParts(new Date(ms))
  const field = (type: Intl.DateTimeFormatPartTypes): number => {
    const found = parts.find((part) => part.type === type)
    return found ? Number(found.value) : 0
  }
  return {
    year: field('year'),
    month: field('month'),
    day: field('day'),
    hour: field('hour'),
    minute: field('minute'),
    second: field('second')
  }
}

/** Milliseconds `ZONE` is ahead of UTC at a given instant. */
function offsetAt(ms: number): number {
  const c = civilOf(ms)
  // civilOf has no sub-second resolution, so compare against the whole second
  // the instant falls in or every offset comes out a few hundred ms wrong -
  // harmless for a raw offset, fatal once `instantOf` subtracts it.
  return Date.UTC(c.year, c.month - 1, c.day, c.hour, c.minute, c.second) - Math.floor(ms / 1000) * 1000
}

/**
 * The inverse of `civilOf`: the instant at which `ZONE`'s wall clock reads
 * these fields.
 *
 * Two passes, because the offset needed to do the conversion is a function of
 * the answer. The first pass uses the offset at the same numbers read as UTC -
 * always within a couple of hours of correct - and the second uses the offset
 * at that result, which is the right one unless the requested wall-clock time
 * is inside a DST gap and does not exist at all. In that case any answer is a
 * fiction; this returns a real instant an hour off rather than NaN, which is
 * the failure mode that keeps a countdown ticking.
 */
export function instantOf(c: Civil): number {
  const asUtc = Date.UTC(c.year, c.month - 1, c.day, c.hour, c.minute, c.second)
  const first = asUtc - offsetAt(asUtc)
  return asUtc - offsetAt(first)
}

function daysInMonth(year: number, month: number): number {
  // Day 0 of the following month is the last day of this one.
  return new Date(Date.UTC(year, month, 0)).getUTCDate()
}

/**
 * The same wall-clock time, `count` calendar months later (or earlier).
 *
 * Clamps the day, so 31 January plus one month is 28 February rather than
 * rolling into March the way `setMonth` would. This matters for a six-year
 * countdown started on a 31st: without the clamp the target lands on the 1st or
 * 2nd of the wrong month and the anchor date quietly stops being the anchor.
 */
export function addMonths(ms: number, count: number): number {
  const c = civilOf(ms)
  const totalMonths = c.year * 12 + (c.month - 1) + count
  const year = Math.floor(totalMonths / 12)
  const month = totalMonths - year * 12 + 1
  const day = Math.min(c.day, daysInMonth(year, month))
  const whole = instantOf({ year, month, day, hour: c.hour, minute: c.minute, second: c.second })
  // Carry the sub-second remainder across. Dropping it would make the seconds
  // field of the countdown jitter by up to a second every time the month
  // component changed.
  return whole + (ms - Math.floor(ms / 1000) * 1000)
}

/**
 * The same wall-clock time, `count` calendar days later.
 *
 * Calendar days, not 86,400,000 ms: on the two days a year the clocks change,
 * a local day is 23 or 25 hours long. Adding a fixed 24 hours across one of
 * those would show "1 day 23 hours" where the calendar says two days, one
 * night a year, for one month out of every six-year run. Rare enough to never
 * be caught by hand, so it is handled here instead.
 */
export function addDays(ms: number, count: number): number {
  const c = civilOf(ms)
  const shifted = new Date(Date.UTC(c.year, c.month - 1, c.day + count))
  const whole = instantOf({
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
    hour: c.hour,
    minute: c.minute,
    second: c.second
  })
  return whole + (ms - Math.floor(ms / 1000) * 1000)
}
